import { ReactNode } from "react";
import Chip from "@/components/Chip";
import pluralize from "@/lib/pluralize";
import type { BulkSelectionProps } from "@/hooks/useBulkSelection";

/**
 * Props for TableFooter component
 * @property pagination - Pagination component (automatically hidden when isSelectedOnly is true)
 * @property rowSelectionCount - Number of selected rows (optional, can use bulkSelection instead)
 * @property onShowSelectedOnly - Handler for "Show Selected Only" chip toggle (optional, can use bulkSelection instead)
 * @property isSelectedOnly - Whether "Show Selected Only" is currently active (optional, can use bulkSelection instead)
 * @property bulkSelection - Grouped bulk selection props from useBulkSelection hook (optional convenience)
 * @property totalRowCount - Total number of rows in the table (used for auto row count when pagination not provided)
 */
export interface TableFooterProps {
  pagination?: ReactNode;
  rowSelectionCount?: number;
  onShowSelectedOnly?: () => void;
  isSelectedOnly?: boolean;
  bulkSelection?: BulkSelectionProps;
  totalRowCount?: number;
}

/**
 * TableFooter component provides consistent footer layout for tables:
 * - Simple mode: Pagination or auto row count (right-aligned)
 * - Bulk selection mode: Row count + "Show Selected Only" chip (left) + pagination/row count (right)
 *
 * **Auto Row Count:**
 * - If no pagination provided but totalRowCount is set, automatically shows "# rows"
 * - Uses same styling as Pagination component (text-label-large)
 *
 * **Desktop:**
 * - Single row with flex layout
 * - Selection controls on left, pagination on right
 *
 * **Mobile:**
 * - Selection controls stack above pagination
 * - Maintains consistent spacing
 *
 * @example
 * ```tsx
 * // Pagination footer
 * <TableFooter pagination={<Pagination {...props} />} />
 *
 * // Client-side table with auto row count
 * <TableFooter totalRowCount={50} />
 *
 * // Bulk selection footer (individual props)
 * <TableFooter
 *   rowSelectionCount={12}
 *   onShowSelectedOnly={() => setShowOnly(!showOnly)}
 *   isSelectedOnly={showOnly}
 *   totalRowCount={50}
 * />
 *
 * // Bulk selection footer (using useBulkSelection hook)
 * const { bulkSelectionProps } = useBulkSelection();
 * <TableFooter
 *   pagination={<Pagination {...props} />}
 *   {...bulkSelectionProps}
 * />
 * ```
 */
export function TableFooter({
  pagination,
  rowSelectionCount = 0,
  onShowSelectedOnly,
  isSelectedOnly = false,
  bulkSelection,
  totalRowCount
}: TableFooterProps) {
  const count = bulkSelection?.rowSelectionCount ?? rowSelectionCount;
  const onShowSelected = bulkSelection?.onShowSelectedOnly ?? onShowSelectedOnly;
  const selectedOnly = bulkSelection?.isSelectedOnly ?? isSelectedOnly;

  const hasRowSelection = count > 0;

  const displayContent =
    (!selectedOnly && pagination) ||
    (!hasRowSelection && totalRowCount !== undefined ? (
      <div className="text-label-large">
        {totalRowCount} {pluralize(totalRowCount, "row")}
      </div>
    ) : null);

  return (
    <div className={`${hasRowSelection ? "justify-between" : "justify-end"} flex flex-col md:flex-row md:items-center`}>
      {hasRowSelection && (
        <div className="flex items-center gap-2 mb-4 md:mb-0">
          <span className="text-label-large">
            {count} {pluralize(count, "row")} selected
          </span>
          {onShowSelected && <Chip onClick={onShowSelected} label="Show Selected Only" selected={selectedOnly} />}
        </div>
      )}
      {displayContent}
    </div>
  );
}
